import type { ColTypeDef } from 'ag-grid-community';
import { useNumberColumnType } from 'components/shared/Blotter/Grid/Hooks/useNumberColumnType';
import { usePercentColumnType } from 'components/shared/Blotter/Grid/Hooks/usePercentColumnType';
import { useYesNoColumnType } from 'components/shared/Blotter/Grid/Hooks/useStatusColumnType';
import { useStringArrayColumnType } from 'components/shared/Blotter/Grid/Hooks/useStringArrayColumnType';
import { useClientSideStringColumnType } from 'components/shared/Blotter/Grid/Hooks/useStringColumnType';
import { useMemo } from 'react';

import { useBooleanColumnType } from './useBooleanColumnType';
import { useClientSideCurrencyColumnType } from './useCurrencyColumnType';
import { useDateColumnType } from './useDateColumnType';
import { useDateOrTimeColumnType } from './useDateOrTimeColumnType';
import { useDateTimeColumnType } from './useDateTimeColumnType';
import { dragColumnType } from './useDragColumnType';
import { useClientSideUnderlyingColumnType } from './useUnderlyingColumnType';

const noCustomCols = {};

export function useClientSideDefaultColumnTypes<Data>(
  customColumns: Record<string, ColTypeDef<Data>> = noCustomCols,
  handleValuesUpdate = false,
): Record<string, ColTypeDef<Data>> {
  const booleanColumn = useBooleanColumnType<Data>();
  const dateColumn = useDateColumnType<Data>();
  const dateTimeColumn = useDateTimeColumnType<Data>();
  const dateOrTimeColumn = useDateOrTimeColumnType<Data>();
  const stringColumn = useClientSideStringColumnType<Data>(handleValuesUpdate);
  const stringArrayColumn = useStringArrayColumnType<Data>(handleValuesUpdate);
  const underlyingColumn = useClientSideUnderlyingColumnType<Data>(handleValuesUpdate);
  const currencyColumn = useClientSideCurrencyColumnType<Data>(handleValuesUpdate);
  const numberColumn = useNumberColumnType<Data>(4);
  const percentColumn = usePercentColumnType<Data>(4);
  const yesNoColumn = useYesNoColumnType<Data>();

  return useMemo<Record<string, ColTypeDef<Data>>>(
    () => ({
      boolean: booleanColumn,
      date: dateColumn,
      datetime: dateTimeColumn,
      dateOrTime: dateOrTimeColumn,
      drag: dragColumnType as ColTypeDef<Data>,
      string: stringColumn,
      stringArray: stringArrayColumn,
      underlyings: underlyingColumn,
      currency: currencyColumn,
      number: numberColumn,
      percent: percentColumn,
      yesNo: yesNoColumn,
      ...customColumns,
    }),
    [
      booleanColumn,
      dateColumn,
      dateTimeColumn,
      dateOrTimeColumn,
      stringColumn,
      stringArrayColumn,
      underlyingColumn,
      currencyColumn,
      numberColumn,
      percentColumn,
      yesNoColumn,
      customColumns,
    ],
  );
}
